import { pagination } from '../pagination'

interface SearchIndexOptions {
  accessToken: string
  index: string
  endpoint: URL
  useCache?: boolean
  assignmentSearch?: boolean
}

export interface SearchIndexResult<T> {
  ok: boolean
  total: number
  page: number
  pages: number
  pageSize: number
  hits: T[]
}

export interface SearchIndexError {
  ok: false
  errorCode: number
  errorMessage: string
  total: 0
  pages: 0
  hits: never[]
}

export type SearchIndexResponse<T> = SearchIndexError | SearchIndexResult<T>

interface RawSearchHits<T> {
  hits?: {
    total?: { value?: number }
    hits?: T[]
  }
}

/**
 * @deprecated This function is deprecated and will be removed in future versions.
 * TODO: use Twirp api and wrap in a hook #ELE-1171
 * @param search - object
 * @param options - SearchIndexOptions
 * @param page - number Optional, defaults to 1
 * @param size - number Optionally wanted page size, defaults to 100
 * @returns Promise<SearchIndexResponse>
 */
export async function searchIndex<T>(search: object, options: SearchIndexOptions, page: number = 1, size: number = 100): Promise<SearchIndexResponse<T>> {
  const endpoint = options?.assignmentSearch
    ? options.endpoint
    : new URL(`${options.index.replace(/[/-]/g, '_')}/_search`, options.endpoint)
  const { from, pageSize } = pagination({
    page,
    size
  })

  const body = JSON.stringify({
    from,
    size: pageSize,
    ...search
  })

  const response = await fetch(endpoint.href, {
    method: 'POST',
    headers: headers(options.accessToken),
    body
  })

  if (response.status !== 200) {
    return responseError(response.status, response.statusText)
  }

  try {
    const body = await response.json() as RawSearchHits<T>

    const total = body?.hits?.total?.value || 0
    const hits = body?.hits?.hits?.length || 0

    const result: SearchIndexResult<T> = {
      ok: true,
      total,
      page: page || 1,
      pages: hits > 0 ? Math.ceil(total / pageSize) : 0,
      pageSize,
      hits: body?.hits?.hits ?? []
    }

    return result
  } catch (ex: unknown) {
    return responseError(0, ex instanceof Error && ex.message ? ex.message : 'Error message not defined')
  }
}


function headers(accessToken: string): Record<string, string> {
  return {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json'
  }
}

function responseError(errorCode: number, errorMessage: string): SearchIndexError {
  return {
    ok: false,
    errorCode,
    errorMessage,
    total: 0,
    pages: 0,
    hits: []
  }
}
